import React, { useState, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Quill's standard theme
import { AppBar, Toolbar, Typography, Button, Container, Paper, Box } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter } from 'react-router-dom';


const App = () => {
  const [isListening, setIsListening] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const quillRef = useRef(null); // Reference for Quill editor
  const cursorPosition = useRef(0); // To track cursor position

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognition();
  recognition.lang = 'te-IN'; // Telugu Language Code

  // Start listening to speech
  const startListening = () => {
    setIsListening(true);
    recognition.start();

    recognition.onresult = (event) => {
      const speechToText = event.results[0][0].transcript;
      insertTextAtCursor(speechToText); // Insert transcribed text at cursor position
    };

    recognition.onerror = (event) => {
      console.error(event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false); // Stop listening when recognition ends
    };
  };

  const stopListening = () => {
    setIsListening(false);
    recognition.stop();
  };

  // Function to insert text at the current cursor position in the Quill editor
  const insertTextAtCursor = (text) => {
    const quill = quillRef.current.getEditor();
    const currentSelection = quill.getSelection(); // Get the current selection

    // If there's no selection, set the cursor to the end of the text
    const currentPosition = currentSelection ? currentSelection.index : quill.getLength();
    quill.insertText(currentPosition, text);
    quill.setSelection(currentPosition + text.length); // Move the cursor to the end of inserted text
    cursorPosition.current = currentPosition + text.length;
  };

  // Update the editor content and cursor position as the user types or clicks
  const handleEditorChange = (content, delta, source, editor) => {
    setEditorContent(content);
    cursorPosition.current = editor.getSelection()?.index || 0; // Update cursor position
  };


  return (
    <BrowserRouter basename="/voice-type">
    <Box
    sx={{
        flexGrow: 1,
        backgroundColor: '#f9f7f6',
        minHeight: '100vh',
        padding: '32px', // Default padding for larger screens (4 in rem or px, depending on theme settings)
        paddingTop: '50px',
        display: 'flex',
        flexDirection: 'column',
        '@media (max-width: 600px)': {
        padding: '8px', // Reduced padding for mobile devices
        paddingTop: '20px',
        },
    }}
    >
    {/* Header with Company Logo */}
    <AppBar position="static" sx={{ backgroundColor: 'transparent', boxShadow: 'none', marginBottom: '20px' }}>
        <Toolbar sx={{ justifyContent: 'center' }}>
        <a href="https://telugu.perceptive-analytics.com">
            <img
            src={`${process.env.PUBLIC_URL}/perceptive.png`}
            alt="Perceptive Analytics Logo"
            style={{ width: '175px', marginRight: '20px' }}
            />
        </a>
        </Toolbar>
    </AppBar>

    {/* Centered Heading with Decoration */}
    <Typography
        variant="h4"
        component="div"
        sx={{
        textAlign: 'center',
        color: '#EFBC9B',
        fontWeight: 'bold',
        letterSpacing: '2px',
        textTransform: 'uppercase',
        marginBottom: '50px',
        textDecoration: 'italic',
        marginTop: '20px',
        }}
    >
        Telugu Voice Typing
    </Typography>

    {/* Microphone Button */}
    <Box
        sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '20px',
        flexGrow: 1,
        position: 'relative',
        }}
    >
        <Button
        onClick={isListening ? stopListening : startListening}
        sx={{
            position: 'relative',
            zIndex: 2,
            fontSize: '2.5rem',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            border: 'none',
            color: '#fff',
            backgroundColor: isListening ? '#f67280' : '#99b898',
            '&:hover': {
            backgroundColor: isListening ? '#e57373' : '#88d498',
            },
            animation: isListening ? 'pulse 1.5s infinite' : 'none',
            '&::before': {
            content: '""',
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            width: '140px',
            height: '140px',
            borderRadius: '50%',
            border: isListening ? '5px solid rgba(246, 114, 128, 0.5)' : 'none',
            animation: isListening ? 'wave 2s infinite' : 'none',
            zIndex: 1,
            },
            '&::after': {
            content: '""',
            position: 'absolute',
            top: '-30px',
            left: '-30px',
            width: '160px',
            height: '160px',
            borderRadius: '50%',
            border: isListening ? '3px solid rgba(246, 114, 128, 0.3)' : 'none',
            animation: isListening ? 'wave 2.5s infinite' : 'none',
            zIndex: 0,
            },
        }}
        >
        {isListening ? <MicOffIcon fontSize="large" /> : <MicIcon fontSize="large" />}
        </Button>
        <Typography sx={{ marginTop: '10px', color: '#333' }}>
        {isListening ? 'Listening...' : 'Click the mic to start speaking'}
        </Typography>
    </Box>

    {/* Add CSS keyframes for the wavy effect */}
    <style jsx>{`
        @keyframes wave {
        0% {
            transform: scale(1);
            opacity: 1;
        }
        100% {
            transform: scale(1.5);
            opacity: 0;
        }
        }

        @keyframes pulse {
        0% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
        }
        }
    `}</style>

    {/* Quill Rich Text Editor */}
    <Container maxWidth="md" sx={{ flexGrow: 1 }}>
        <Paper
            elevation={3}
            sx={{
            padding: 3,
            minHeight: '500px',
            textAlign: 'left',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            '@media (max-width: 600px)': {
                padding: 2, // Reduce padding on mobile screens
                minHeight: '300px', // Adjust height for mobile
            },
            }}
        >
            <ReactQuill
            theme="snow"
            value={editorContent}
            onChange={handleEditorChange}
            ref={quillRef}
            modules={editorModules}
            formats={editorFormats}
            style={{
                minHeight: '300px', // Increase the default space for the editor
                '@media (max-width: 600px)': {
                minHeight: '200px', // Adjust for smaller devices
                },
            }}
            />
        </Paper>
        </Container>

        {/* Add custom CSS for responsive toolbar and text area */}
        <style jsx>{`
        .ql-toolbar {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            padding: 8px;
        }

        /* For smaller screens, collapse some toolbar buttons into a dropdown */
        @media (max-width: 600px) {
            .ql-toolbar {
            flex-direction: column;
            align-items: start;
            }
            
            .ql-toolbar .ql-formats {
            margin-bottom: 8px;
            display: flex;
            flex-wrap: wrap;
            }

            /* Optional: You can hide less frequently used buttons */
            .ql-toolbar .ql-formats .ql-header, 
            .ql-toolbar .ql-formats .ql-color,
            .ql-toolbar .ql-formats .ql-code-block {
            display: none;
            }
        }

        /* Adjust the editor area on mobile for better UX */
        .ql-editor {
            min-height: 300px; /* Default height increased */
            font-size: 1rem;
        }

        @media (max-width: 600px) {
            .ql-editor {
            min-height: 200px; /* Adjust for mobile */
            font-size: 0.875rem; /* Slightly smaller font on mobile */
            }
        }
        `}</style>

    {/* Footer */}
    <Box
        component="footer"
        sx={{
        width: '100%',
        backgroundColor: '#C8B6FF',
        color: '#fff',
        textAlign: 'center',
        padding: '10px 0',
        marginTop: '50px',
        }}
    >
        <Typography variant="body1">Perceptive Analytics © 2024</Typography>
    </Box>
    </Box>
    </BrowserRouter>
  )
};


// Quill.js modules and formats for formatting options
const editorModules = {
    toolbar: [
      [{ header: '1' }, { header: '2' }, { font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image', 'video'],
      [{ align: [] }],
      ['clean'],
    ],
  };
  
  const editorFormats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'align',
    'link',
    'image',
    'video',
  ];
  
  export default App;
  